/**
 * Vehicles (Fuhrpark) — Hauptseite mit Liste, Filter, CRUD, Import/Export.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Car, Plus, Search, Upload, Download, Pencil, Trash2, History,
  AlertTriangle, MapPin, Snowflake, Sun, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI, branchesAPI, locationsAPI, employeesAPI, companyAPI } from '../lib/api';
import { OrgUnitFilter, orgUnitToLegacyParams } from '../components/OrgUnitFilter';
import { useAuth } from '../contexts/AuthContext';
import { VehicleDialog } from './vehicles/VehicleDialog';
import { VehicleHistoryDialog } from './vehicles/VehicleHistoryDialog';
import { VehicleImportDialog } from './vehicles/VehicleImportDialog';
import { VehicleEventsDialog } from './vehicles/VehicleEventsDialog';

const STATUS_LABELS = {
  active: { label: 'Aktiv', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  workshop: { label: 'Werkstatt', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  sold: { label: 'Verkauft', className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  decommissioned: { label: 'Stillgelegt', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

const TIRE_LABELS = {
  summer: { label: 'Sommer', icon: Sun, color: 'text-amber-600' },
  winter: { label: 'Winter', icon: Snowflake, color: 'text-blue-600' },
  allseason: { label: 'Ganzjahr', icon: Sun, color: 'text-emerald-600' },
};

const USAGE_LABELS = {
  company_car: { label: 'Dienstwagen', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  pool: { label: 'Poolfahrzeug', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  service: { label: 'Werkstatt-/Servicewagen', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
};

function tuvBadge(date) {
  if (!date) return null;
  const today = new Date();
  const due = new Date(date);
  const days = Math.floor((due - today) / 86400000);
  if (days < 0) return <Badge className="bg-red-100 text-red-800">TÜV abgelaufen</Badge>;
  if (days < 30) return <Badge className="bg-amber-100 text-amber-800">TÜV in {days} T.</Badge>;
  return <span className="text-xs text-muted-foreground">{due.toLocaleDateString('de-DE')}</span>;
}

export default function Vehicles() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('fleet', 'edit');

  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrgUnit, setFilterOrgUnit] = useState('all');
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [filterHolder, setFilterHolder] = useState('all'); // 'all' | 'none' | 'assigned'

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyVehicle, setHistoryVehicle] = useState(null);
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
  const [eventsVehicle, setEventsVehicle] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterStatus !== 'all') params.status = filterStatus;
      Object.assign(params, orgUnitToLegacyParams(orgTree, filterOrgUnit));
      if (filterHolder === 'none') params.holder_id = 'none';
      const v = await vehiclesAPI.list(params);
      setVehicles(v.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterOrgUnit, filterHolder, orgTree]);

  useEffect(() => {
    (async () => {
      try {
        const [b, l, e, t] = await Promise.all([
          branchesAPI.getAll(),
          locationsAPI.getAll(),
          employeesAPI.getAll(),
          companyAPI.getOrgTree().catch(() => ({ data: { tree: [], levels: [] } })),
        ]);
        setBranches(b.data);
        setLocations(l.data);
        setEmployees(e.data);
        setOrgTree(t.data?.tree || []);
        setOrgLevels(t.data?.levels || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = () => { setSelectedVehicle(null); setDialogOpen(true); };
  const onEdit = (v) => { setSelectedVehicle(v); setDialogOpen(true); };
  const onShowHistory = (v) => { setHistoryVehicle(v); setHistoryDialogOpen(true); };
  const onShowEvents = (v) => { setEventsVehicle(v); setEventsDialogOpen(true); };

  const onDelete = async (v) => {
    if (!window.confirm(`Fahrzeug ${v.license_plate} wirklich löschen?\nDer komplette Verlauf wird ebenfalls entfernt.`)) return;
    try {
      await vehiclesAPI.delete(v.id);
      toast.success('Fahrzeug gelöscht');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const onExport = async () => {
    try {
      const res = await vehiclesAPI.exportXlsx();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fuhrpark_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const counts = {
    total: vehicles.length,
    active: vehicles.filter(v => v.status === 'active').length,
    workshop: vehicles.filter(v => v.status === 'workshop').length,
    noHolder: vehicles.filter(v => !v.holder_employee_id).length,
    tuvSoon: vehicles.filter(v => {
      if (!v.tuv_due) return false;
      const days = (new Date(v.tuv_due) - new Date()) / 86400000;
      return days < 30;
    }).length,
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="vehicles-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Car className="h-6 w-6 text-primary" />
              Fuhrpark
            </h1>
            <p className="text-muted-foreground text-sm">
              Fahrzeugverwaltung mit Halter, Standorten, Verlauf und Reifenlager
            </p>
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="btn-import-vehicles">
                <Upload className="h-4 w-4 mr-2" /> Excel-Import
              </Button>
              <Button variant="outline" onClick={onExport} data-testid="btn-export-vehicles">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
              <Button onClick={onCreate} data-testid="btn-new-vehicle">
                <Plus className="h-4 w-4 mr-2" /> Neues Fahrzeug
              </Button>
            </div>
          )}
        </div>

        {/* Statistik-Kacheln */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Gesamt</p>
            <p className="text-2xl font-bold">{counts.total}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Aktiv</p>
            <p className="text-2xl font-bold text-green-600">{counts.active}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Werkstatt</p>
            <p className="text-2xl font-bold text-amber-600">{counts.workshop}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Ohne Halter</p>
            <p className="text-2xl font-bold text-slate-600">{counts.noHolder}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">TÜV &lt; 30 T.</p>
            <p className={`text-2xl font-bold ${counts.tuvSoon > 0 ? 'text-red-600' : 'text-green-600'}`}>{counts.tuvSoon}</p>
          </CardContent></Card>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Kennzeichen, Marke, Modell, FIN..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="filter-search"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="workshop">Werkstatt</SelectItem>
                  <SelectItem value="sold">Verkauft</SelectItem>
                  <SelectItem value="decommissioned">Stillgelegt</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-[240px]">
                <OrgUnitFilter
                  tree={orgTree}
                  levels={orgLevels}
                  value={filterOrgUnit}
                  onChange={setFilterOrgUnit}
                  showLabel={false}
                  testId="filter-org-unit"
                />
              </div>
              <Select value={filterHolder} onValueChange={setFilterHolder}>
                <SelectTrigger className="w-[160px]" data-testid="filter-holder"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Halter: Alle</SelectItem>
                  <SelectItem value="none">Ohne Halter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabelle */}
        <Card>
          <CardHeader>
            <CardTitle>Fahrzeuge ({vehicles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Car className="h-12 w-12 mx-auto opacity-30 mb-3" />
                <p>Keine Fahrzeuge gefunden.</p>
                {canEdit && <Button variant="outline" className="mt-4" onClick={onCreate}><Plus className="h-4 w-4 mr-2" />Erstes Fahrzeug anlegen</Button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kennzeichen</TableHead>
                      <TableHead>Marke / Modell</TableHead>
                      <TableHead>Halter</TableHead>
                      <TableHead>Standort</TableHead>
                      <TableHead>Verwendung</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reifen</TableHead>
                      <TableHead>TÜV</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map(v => {
                      const status = STATUS_LABELS[v.status] || STATUS_LABELS.active;
                      const tire = TIRE_LABELS[v.current_tires];
                      const TireIcon = tire?.icon;
                      return (
                        <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                          <TableCell className="font-mono font-medium">{v.license_plate}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{v.brand} {v.model}</p>
                              {v.variant && <p className="text-xs text-muted-foreground">{v.variant}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            {v.holder_name || <span className="text-muted-foreground text-xs">— ohne Halter —</span>}
                          </TableCell>
                          <TableCell>
                            {v.location_name ? (
                              <span className="text-sm flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                {v.location_name}
                              </span>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            {USAGE_LABELS[v.usage] ? (
                              <Badge variant="outline" className={USAGE_LABELS[v.usage].className}>
                                {USAGE_LABELS[v.usage].label}
                              </Badge>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            <Badge className={status.className}>{status.label}</Badge>
                          </TableCell>
                          <TableCell>
                            {tire ? (
                              <span className={`text-xs flex items-center gap-1 ${tire.color}`}>
                                <TireIcon className="h-3 w-3" />
                                {tire.label}
                                {v.tire_storage_location && <span className="text-muted-foreground">· {v.tire_storage_location}</span>}
                              </span>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>{tuvBadge(v.tuv_due)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => onShowEvents(v)} title="Schäden & Wartung" data-testid={`btn-events-${v.id}`}>
                                <Wrench className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => onShowHistory(v)} title="Verlauf" data-testid={`btn-history-${v.id}`}>
                                <History className="h-4 w-4" />
                              </Button>
                              {canEdit && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => onEdit(v)} title="Bearbeiten" data-testid={`btn-edit-${v.id}`}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => onDelete(v)} title="Löschen" className="text-red-600" data-testid={`btn-delete-${v.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <VehicleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          vehicle={selectedVehicle}
          employees={employees}
          branches={branches}
          locations={locations}
          onSaved={load}
        />
        <VehicleHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          vehicle={historyVehicle}
          canEdit={canEdit}
          onChanged={load}
        />
        <VehicleImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          onImported={load}
        />
        <VehicleEventsDialog
          open={eventsDialogOpen}
          onOpenChange={setEventsDialogOpen}
          vehicle={eventsVehicle}
          canEdit={canEdit}
        />
      </div>
    </DashboardLayout>
  );
}
